'use client';

import React, { forwardRef, useImperativeHandle, useRef, useEffect, useState } from 'react';
import { useBinanceStream } from './useBinanceStream';
import { colors, baseStyles, formatNum } from './SharedUI';

const LeverageSimulator = forwardRef(({ symbol }, ref) => {
  // ==========================================
  // 1. STATE & HOOK KẾT NỐI MẠNG
  // ==========================================
  const { streamData, wsStatus } = useBinanceStream(symbol.toLowerCase(), true);
  const livePrice = streamData.price || 0;

  const [position, setPosition] = useState('LONG'); // 'LONG' | 'SHORT'
  const [walletBalance, setWalletBalance] = useState(5000);
  const [margin, setMargin] = useState(1000);
  const [entryPrice, setEntryPrice] = useState(0);
  const [leverage, setLeverage] = useState(10);

  // Tạo Két sắt
  const latestSimRef = useRef({ margin: 1000, leverage: 10 });

  useEffect(() => {
    latestSimRef.current = { margin, leverage };
  }, [margin, leverage]);

  useImperativeHandle(ref, () => ({
    getSnapshot: () => latestSimRef.current
  }));


  // ==========================================
  // 2. TÍNH TOÁN CÁC THÔNG SỐ (QUANT LOGIC)
  // ==========================================
  // Quy mô vị thế (Đô la)
  const notionalSize = margin * leverage;
  // Số lượng Coin nắm giữ
  const qty = entryPrice > 0 ? notionalSize / entryPrice : 0;

  // Tính PnL (Lợi nhuận) và ROE (Tỷ suất trên vốn)
  let pnl = 0;
  if (entryPrice > 0 && livePrice > 0) {
    if (position === 'LONG') {
      pnl = qty * (livePrice - entryPrice);
    } else {
      pnl = qty * (entryPrice - livePrice);
    }
  }
  const roe = margin > 0 ? (pnl / margin) * 100 : 0;

  // Tính Giá Thanh Lý
  let isoLiq = 0;
  let crossLiq = 0;
  
  if (entryPrice > 0) {
    if (position === 'LONG') {
      isoLiq = entryPrice * (1 - 1 / leverage);
      crossLiq = Math.max(0, entryPrice - (walletBalance / qty));
    } else {
      isoLiq = entryPrice * (1 + 1 / leverage);
      crossLiq = entryPrice + (walletBalance / qty);
    }
  }

  // ==========================================
  // 3. LOGIC THANH CẢNH BÁO CHÁY TÀI KHOẢN (GAUGE)
  // ==========================================
  let gaugePct = 50; 
  if (entryPrice > 0 && isoLiq > 0 && livePrice > 0) {
    if (position === 'LONG') {
      const danger = isoLiq;
      const safe = entryPrice + (entryPrice - isoLiq); 
      const range = safe - danger;
      gaugePct = ((livePrice - danger) / range) * 100;
    } else {
      const danger = isoLiq; 
      const safe = entryPrice - (isoLiq - entryPrice);
      const range = danger - safe;
      gaugePct = ((danger - livePrice) / range) * 100;
    }
  }
  gaugePct = Math.max(0, Math.min(100, gaugePct));

  // ==========================================
  // 4. HANDLERS GIAO DIỆN
  // ==========================================
  const snapLivePrice = () => {
    if (livePrice > 0) {
      setEntryPrice(livePrice);
    }
  };

  const isProfit = pnl >= 0;
  const pnlColor = isProfit ? colors.green : colors.red;

  return (
    <div style={{ ...baseStyles.card, maxWidth: '600px', margin: '0 auto', boxSizing: 'border-box' }}>
      
      <div style={{ textAlign: 'center', marginBottom: '20px', borderBottom: `1px dashed ${colors.border}`, paddingBottom: '16px' }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', color: colors.warn }}>⚖️ MÁY TÍNH VỊ THẾ & THANH LÝ (LIVE)</h2>
        <div style={{ fontSize: '12px', color: wsStatus.color }}>
          {wsStatus.text} - Đang soi: <strong>{symbol.toUpperCase()}</strong>
        </div>
      </div>

      {/* CHỌN CHIỀU ĐÁNH */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button 
          onClick={() => setPosition('LONG')}
          style={{ flex: 1, padding: '12px', borderRadius: '8px', border: `2px solid ${position === 'LONG' ? colors.green : colors.border}`, backgroundColor: position === 'LONG' ? 'rgba(34, 197, 94, 0.1)' : 'transparent', color: position === 'LONG' ? colors.green : colors.textSub, fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s' }}
        >
          🚀 LONG (Mua Lên)
        </button>
        <button 
          onClick={() => setPosition('SHORT')}
          style={{ flex: 1, padding: '12px', borderRadius: '8px', border: `2px solid ${position === 'SHORT' ? colors.red : colors.border}`, backgroundColor: position === 'SHORT' ? 'rgba(239, 68, 68, 0.1)' : 'transparent', color: position === 'SHORT' ? colors.red : colors.textSub, fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s' }}
        >
          🩸 SHORT (Bán Khống)
        </button>
      </div>

      {/* QUẢN LÝ VỐN */}
      <div style={{ backgroundColor: 'rgba(0,0,0,0.3)', padding: '16px', borderRadius: '8px', marginBottom: '20px', border: `1px solid ${colors.border}` }}>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '12px', color: colors.textSub, display: 'block', marginBottom: '4px' }}>🏦 Tổng Số Dư Ví (Wallet Balance - USD):</label>
          <input 
            type="number" value={walletBalance} onChange={(e) => setWalletBalance(Number(e.target.value))}
            style={{ ...baseStyles.input, width: '100%', boxSizing: 'border-box' }}
          />
        </div>
        <div>
          <label style={{ fontSize: '12px', color: colors.textSub, display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>💰 Vốn Ký Quỹ Đánh Lệnh (Margin - USD):</span>
            <span style={{ color: margin > walletBalance ? colors.red : colors.textSub }}>
              (Chiếm {((margin / (walletBalance || 1)) * 100).toFixed(1)}% Ví)
            </span>
          </label>
          <input 
            type="number" value={margin} onChange={(e) => setMargin(Number(e.target.value))}
            style={{ ...baseStyles.input, width: '100%', boxSizing: 'border-box', border: margin > walletBalance ? `1px solid ${colors.red}` : `1px solid ${colors.border}` }}
          />
        </div>
      </div>

      {/* THÔNG SỐ LỆNH VÀ GIÁ LIVE */}
      <div style={{ backgroundColor: 'rgba(59, 130, 246, 0.05)', padding: '16px', borderRadius: '8px', marginBottom: '20px', border: `1px solid rgba(59, 130, 246, 0.3)` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontSize: '12px', color: '#3b82f6', fontWeight: 'bold' }}>📡 GIÁ THỊ TRƯỜNG LIVE:</span>
          <strong style={{ fontSize: '18px', color: '#fff' }}>${formatNum(livePrice, livePrice < 1 ? 6 : 2)}</strong>
        </div>
        
        <div>
          <label style={{ fontSize: '12px', color: colors.textSub, display: 'block', marginBottom: '4px' }}>🎯 Giá Vào Lệnh (Entry Price - USD):</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input 
              type="number" value={entryPrice || ''} onChange={(e) => setEntryPrice(Number(e.target.value))} placeholder="Nhập giá hoặc bấm nút bên cạnh..."
              style={{ ...baseStyles.input, flex: 1 }}
            />
            <button 
              onClick={snapLivePrice}
              style={{ padding: '8px 12px', borderRadius: '6px', border: 'none', backgroundColor: '#3b82f6', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}
            >
              ⚡ BẮT GIÁ LIVE
            </button>
          </div>
        </div>
      </div>

      {/* ĐÒN BẨY */}
      <div style={{ backgroundColor: 'rgba(0,0,0,0.3)', padding: '16px', borderRadius: '8px', marginBottom: '20px', border: `1px solid ${colors.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <strong style={{ fontSize: '14px', color: colors.warn }}>🕹️ Đòn Bẩy (Leverage): x{leverage}</strong>
          <span style={{ fontSize: '12px', color: colors.textSub }}>Quy mô vị thế: <strong style={{color:'#fff'}}>${formatNum(notionalSize)}</strong></span>
        </div>
        <input 
          type="range" min="1" max="125" step="1" value={leverage} onChange={(e) => setLeverage(Number(e.target.value))}
          style={{ width: '100%', cursor: 'pointer', accentColor: colors.warn }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: colors.textSub, marginTop: '4px' }}>
          <span>1x</span><span>10x</span><span>20x</span><span>50x</span><span>100x</span><span>125x</span>
        </div>
      </div>

      {/* ========================================== */}
      {/* BẢNG CHẨN ĐOÁN LIVE PNL & RỦI RO           */}
      {/* ========================================== */}
      <div style={{ borderTop: `1px dashed ${colors.border}`, paddingTop: '20px' }}>
        
        {/* THANH LÝ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
          <div style={{ backgroundColor: 'rgba(0,0,0,0.3)', padding: '12px', borderRadius: '6px', borderLeft: `4px solid ${colors.warn}` }}>
            <div style={{ fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>🔒 CHẾ ĐỘ ISOLATED (CÁCH LY) - Mất ${formatNum(margin)}</div>
            <div style={{ fontSize: '14px' }}>💀 Giá Thanh Lý: <strong style={{ color: colors.red }}>${formatNum(isoLiq, isoLiq < 1 ? 6 : 2)}</strong></div>
          </div>
          <div style={{ backgroundColor: 'rgba(0,0,0,0.3)', padding: '12px', borderRadius: '6px', borderLeft: `4px solid ${colors.red}` }}>
            <div style={{ fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>♾️ CHẾ ĐỘ CROSS (CHÉO) - Mất sạch ${formatNum(walletBalance)} ví</div>
            <div style={{ fontSize: '14px' }}>💀 Giá Thanh Lý: <strong style={{ color: colors.red }}>${formatNum(crossLiq, crossLiq < 1 ? 6 : 2)}</strong></div>
          </div>
        </div>

        {/* PNL LIVE */}
        <div style={{ backgroundColor: isProfit ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)', padding: '16px', borderRadius: '8px', border: `1px solid ${pnlColor}`, marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', color: colors.textSub, marginBottom: '8px', fontWeight: 'bold' }}>💵 LÃI / LỖ (PnL) TẠI THỜI ĐIỂM LIVE:</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: pnlColor }}>
              {isProfit ? '+' : ''}${formatNum(pnl)}
            </div>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: pnlColor }}>
              ROE: {isProfit ? '+' : ''}{formatNum(roe)}%
            </div>
          </div>
        </div>

        {/* THANH GAUGE CẢNH BÁO (ISOLATED) */}
        <div style={{ backgroundColor: 'rgba(0,0,0,0.3)', padding: '16px', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 'bold', marginBottom: '8px' }}>
            <span style={{ color: colors.red }}>💀 Vùng Cháy Tài Khoản</span>
            <span style={{ color: colors.green }}>🟢 Vùng An Toàn / Có Lãi</span>
          </div>
          
          <div style={{ position: 'relative', height: '20px', backgroundColor: '#333', borderRadius: '10px', overflow: 'hidden', border: '1px solid #111' }}>
            {/* Vùng Nguy Hiểm (0 -> 50%) */}
            <div style={{ position: 'absolute', left: '0', top: '0', bottom: '0', width: '50%', background: 'linear-gradient(to right, #ef4444, #f59e0b)' }}></div>
            {/* Vùng An Toàn (50 -> 100%) */}
            <div style={{ position: 'absolute', left: '50%', top: '0', bottom: '0', width: '50%', background: 'linear-gradient(to right, #22c55e, #10b981)' }}></div>
            
            {/* Vạch kẻ Entry */}
            <div style={{ position: 'absolute', left: '50%', top: '0', bottom: '0', width: '2px', backgroundColor: '#fff', zIndex: 1 }} title="Giá Vào Lệnh"></div>
            
            {/* Con trỏ Giá Live chạy tung tăng */}
            <div 
              style={{ 
                position: 'absolute', 
                left: `calc(${gaugePct}% - 4px)`, 
                top: '-2px', bottom: '-2px', width: '8px', 
                backgroundColor: '#fff', 
                borderRadius: '4px', 
                boxShadow: '0 0 8px #fff',
                transition: 'left 0.2s ease-out',
                zIndex: 2
              }} 
              title={`Live: $${livePrice}`}
            ></div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: colors.textSub, marginTop: '8px' }}>
            <span>Liq: ${formatNum(isoLiq, isoLiq < 1 ? 6 : 2)}</span>
            <span>Entry: ${formatNum(entryPrice, entryPrice < 1 ? 6 : 2)}</span>
            <span>Khoảng cách an toàn</span>
          </div>
        </div>

      </div>
    </div>
  );
});
export default LeverageSimulator;
